// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

export interface Patient {
  id: string;
  name: string;
  dob: string;
  provider: string;
  appointmentDateTime: string;
  prepStatus: "unopened" | "in-progress" | "completed" | "reviewed";
  hasActiveConversation: boolean;
}

export const PREP_STATUS_LABEL: Record<Patient["prepStatus"], string> = {
  unopened: "Not started",
  "in-progress": "In progress",
  completed: "Ready for review",
  reviewed: "Reviewed",
};

export interface Medication {
  name: string;
  dose: string;
  frequency: string;
  status: "active" | "inactive";
}

export interface Condition {
  name: string;
  onset: string;
  status: "active" | "resolved";
}

export interface LabResult {
  test: string;
  value: string;
  unit: string;
  date: string;
  flag?: "high" | "low" | "normal";
}

export interface Encounter {
  date: string;
  provider: string;
  type: string;
  note: string;
}

export interface Allergy {
  substance: string;
  reaction: string;
  severity: "mild" | "moderate" | "severe";
}

export interface Immunization {
  vaccine: string;
  date: string;
  dose: string;
}

// AI-generated Visit Prep Note. Attached patient-level (latest) and per-encounter
// (that encounter's own note) — identical shape on both.
export interface PrepNote {
  chiefComplaint: string;
  summary: string;
  // Structured patient-facing subjective blocks (reason, history, medications, …).
  sections?: {
    sectionKey: string;
    title: string;
    content: string;
    displayOrder: number;
  }[];
  generatedAt: string;
  assessment?: string;
  differentialDiagnosis?: string[];
  plan?: string[];
  hieFindings?: { source: string; date: string; summary: string }[];
  safetyFlags?: {
    category: string;
    severity: string;
    summary: string;
    evidence: string;
  }[];
}

export interface PatientDetail extends Patient {
  phone: string;
  email: string;
  address: string;
  insurance: string;
  memberId: string;
  bloodType: string;
  /** ISO 8601 timestamp of the last HIE/Particle bundle that landed; null = never. */
  clinicalDataUpdatedAt?: string | null;
  conditions: Condition[];
  medications: Medication[];
  allergies: Allergy[];
  labs: LabResult[];
  encounters: Encounter[];
  immunizations: Immunization[];
  prepNote?: PrepNote;
}

// ── Auth / identity ──────────────────────────────────────────────────────────

// Membership roles stamped as a Firebase custom claim by POST /v1/auth/bootstrap.
// platform_superadmin > qh_admin > org_admin > clinic_staff > provider.
export type Role =
  | "platform_superadmin"
  | "qh_admin"
  | "org_admin"
  | "clinic_staff"
  | "provider";

// Pre-auth home-realm-discovery response from GET /v1/auth/tenant. Public
// endpoint; returns a uniform 200 even for unknown domains (empty tenantId/
// orgSlug — by design, so it can't be scripted to enumerate client orgs).
// `idps` (enabled IdPs per tenant) is a planned fast-follow — absent today, so
// the client defaults to Google for the demo. Kept as string[] for forward
// compat; values are prefixed for custom SAML/OIDC (e.g. 'saml.okta-xyz').
export interface TenantResolution {
  tenantId: string;
  orgSlug: string;
  idps?: string[];
}

// One selectable org in the operator cross-org "act-as" switcher (subset of the
// /admin/orgs control-plane shape). Operators view a client org's data by
// selecting one; the id is sent as X-Org-Context on /v1/* calls.
export interface OrgOption {
  id: string;
  slug: string;
  displayName: string;
}
